import { useInView } from "../hooks/useInView";
import { PhoneMockup } from "./PhoneMockup";

const perks = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z" />
      </svg>
    ),
    title: "Earn Tickets",
    description: "Complete lessons, play the Sim, and take daily quizzes to earn tickets just by using the app.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M21 11.25v8.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 1 0 9.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1 1 14.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z" />
      </svg>
    ),
    title: "Grand Prize Drawings",
    description: "Your tickets enter you in scholarship drawings — prize amounts and dates are set per program.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
      </svg>
    ),
    title: "Weekly Competitions",
    description: "Compete against other students in your program in weekly Sim challenges.",
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
        <path d="M3.75 3v11.25A2.25 2.25 0 0 0 6 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0 1 18 16.5h-2.25m-7.5 0h7.5m-7.5 0-1 3m8.5-3 1 3m0 0 .5 1.5m-.5-1.5h-9.5m0 0-.5 1.5m.75-9 3-3 2.148 2.148A12.061 12.061 0 0 1 16.5 7.605" />
      </svg>
    ),
    title: "Rotating Goals",
    description: "Each week's competition targets a different goal — balance all stats, maximize happiness, net worth, and more.",
  },
];

export function Incentives() {
  const [ref, inView] = useInView();

  return (
    <section id="incentives" className="relative py-24 bg-bg-primary overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16"
        >
          {/* Copy — shown first on mobile, second on desktop */}
          <div className="flex-1 min-w-0 order-1 lg:order-2">
            <p
              className={`text-brand-gold font-semibold tracking-wide uppercase text-sm mb-3 ${
                inView ? "animate-fade-up animate-delay-100" : "start-hidden"
              }`}
            >
              The Incentives
            </p>
            <h2
              className={`text-3xl sm:text-4xl font-bold text-white mb-3 ${
                inView ? "animate-fade-up animate-delay-200" : "start-hidden"
              }`}
            >
              Learn more,{" "}
              <span className="text-brand-gold">earn more</span>
            </h2>
            <p
              className={`text-text-secondary text-lg mb-10 max-w-lg ${
                inView ? "animate-fade-up animate-delay-300" : "start-hidden"
              }`}
            >
              Every action in Dough earns you tickets — and tickets mean real prizes.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {perks.map((perk, i) => (
                <div
                  key={perk.title}
                  className={`flex gap-4 ${
                    inView
                      ? `animate-fade-up animate-delay-${(i + 2) * 100}`
                      : "start-hidden"
                  }`}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-brand-gold/15 border border-brand-gold/30 flex items-center justify-center text-brand-gold">
                    {perk.icon}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold mb-1">
                      {perk.title}
                    </h3>
                    <p className="text-text-secondary text-sm leading-relaxed">
                      {perk.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Phone mockup — shown second on mobile, first on desktop */}
          <div
            className={`flex-shrink-0 order-2 lg:order-1 ${
              inView ? "animate-fade-right animate-delay-100" : "start-hidden"
            }`}
          >
            <PhoneMockup
              src="/images/screenshots/tickets.png"
              alt="Earn tickets and win prizes"
              width={280}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
